const API_URL = import.meta.env.VITE_APP_BASE_AI
// const API_URL = 'https://isafety.zgcsafety.com.cn/aisafety'
// const API_URL= 'http://192.168.8.106:8080'
import useUserStore from "@/stores/user.ts";
import { callDoubaoStream, type DoubaoMessage } from './doubaoClient'

export function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function generateTitle(content: string): string {
  const trimmed = content.trim()
  return trimmed.length > 18 ? trimmed.slice(0, 18) + '...' : trimmed
}
// 下载文件方法
export async function downloadFile(url: string): Promise<void> {
  try {
    const userStore = useUserStore();

    // 使用 fetch 下载文件
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authentication': userStore.user_token
      }
    });

    if (!response.ok) {
      throw new Error('下载失败');
    }

    // 获取文件名
    const contentDisposition = response.headers.get('content-disposition');
    let fileName = 'download.xlsx';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?['"]|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        fileName = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''));
      }
    }

    // 从 URL 中提取文件名作为备选
    const urlObj = new URL(url);
    const urlFileName = urlObj.searchParams.get('fileName');
    if (urlFileName && fileName === 'download.xlsx') {
      fileName = decodeURIComponent(urlFileName);
    }

    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('下载出错:', error);
  }
}
// 创建全局 abort controller (使用 Map 数据结构)
const abortControllers = new Map<string, AbortController>()

// 真实的流式请求函数
export async function getStreamResponse(
    input: string,
    chatId: string,
    onChunk: (text: string) => void,
    enableSearch = false,
    files?: UploadedFile[]
): Promise<void> {
  const userStore = useUserStore()

  // 创建 AbortController
  const abortController = new AbortController()
  abortControllers.set(chatId, abortController)

  try {
    const formData = new FormData()
    formData.append('message', input)
    formData.append('sessionId', chatId)
    formData.append('userId', userStore.user.userId)
    formData.append('roleId', userStore.user.roleId)
    formData.append('enableSearch', enableSearch.toString())

    // 添加文件参数
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const uploadedFile = files[i]
        formData.append('files', uploadedFile.file, uploadedFile.file.name)
      }
      console.log(`添加了 ${files.length} 个文件到请求中`)
    }

    const url = API_URL + '/api/chat/stream'
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': userStore.user_token,
        'timestemp': userStore.timestemp,
      },
      signal: abortController.signal
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    // 检查 Content-Type
    const contentType = response.headers.get('content-type')
    console.log('响应类型:', contentType)
    // 处理流式响应
    const reader = response.body?.getReader()
    const decoder = new TextDecoder('utf-8');
    if (reader) {
      let buffer = '' // 临时缓存回答（用于 SSE 拼接）
      let allBuffer = '' // 完整缓存回答（切换历史记录时可继续加载）
      let done = false

      while (!done && !abortController.signal.aborted) {
        // 每次读取前检查是否已中断
        if (abortController.signal.aborted) {
          console.log('检测到中断，停止读取')
          break
        }

        const result = await reader.read()
        done = result.done
        const value = result.value

        if (done) {
          console.log('流式传输完成')
          break
        }

        // 再次检查中断信号
        if (abortController.signal.aborted) {
          console.log('数据块接收后检测到中断，丢弃此数据')
          break
        }

        const chunk = decoder.decode(value, { stream: true })
        console.log('原始数据块:', chunk)
        buffer += chunk

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed) continue

          if (trimmed.startsWith('data:')) {
            const content = trimmed.slice(5).trim()

            if (content === '[DONE]') {
              console.log('收到结束标记')
              continue
            }

            if (content) {
              allBuffer += content
              onChunk(content)
            }
          } else if (!trimmed.startsWith('event:') && !trimmed.startsWith('id:')) {
            allBuffer += trimmed
            onChunk(trimmed)
          }
        }
      }

      // 清理资源
      if (!done) {
        await reader.cancel()
      }

      // 完成后清除临时缓存
      if (abortController.signal.aborted) {
        console.log('请求被中断，最终缓存内容:', allBuffer)
      } else {
        console.log('回答完成，总缓存长度:', allBuffer.length)
      }
    }
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      console.log('请求已取消')
    } else {
      console.error('流式请求失败:', error)
      throw error
    }
  } finally {
    abortControllers.delete(chatId)
  }
}

export interface UploadedFile {
  file: File
  name: string
}

// ---- Simple Markdown Renderer ----

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function processInline(text: string): string {
  return escapeHtml(text)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')
}

export function renderMarkdown(raw: string): string {
  // 初始化全局下载函数（如果尚未初始化）
  if (!(window as any).portDownload) {
    (window as any).portDownload = async (url: string) => {
      await downloadFile(url);
    };
  }
// 将 \n 转换为实际的换行符
  let normalizedContent = raw.replace(/\\n/g, '\n')
  // 移除 <br> 标签，避免显示为文本
  normalizedContent = normalizedContent.replace(/<br\s*\/?>/gi, '\n')

  const lines = normalizedContent.split('\n')
  // const lines = raw.split('\n')
  const cleanedLines = lines.map(line => {
    const trimmedLine = line.trim()

    // 移除行首的 ### 标记（如果不是标准标题格式）
    if (trimmedLine.startsWith('###') && !trimmedLine.startsWith('### ')) {
      return trimmedLine.slice(3).trim()
    }

    // 移除行首的 > 标记（如果不是标准引用格式）
    if (trimmedLine.startsWith('>') && !trimmedLine.startsWith('> ')) {
      return trimmedLine.slice(1).trim()
    }

    // 如果行以 | 开头但不是表格（前后没有匹配的 |），移除开头的 |
    if (line.startsWith('|') && !line.endsWith('|') && line.indexOf('|', 1) === -1) {
      return line.slice(1).trim()
    }

    // 如果行中包含 |...| 但不是完整的表格行，移除这些竖线
    if (!line.startsWith('|') && line.includes('|')) {
      return line.replace(/\|([^|]+)\|/g, '$1')
    }

    return line
  })
  normalizedContent = cleanedLines.join('\n')

  const outputLines = normalizedContent.split('\n')
  const output: string[] = []
  let inCode = false
  let codeLang = ''
  const codeLines: string[] = []
  let listItems: string[] = []
  let inOrderedList = false
  let orderedListItems: string[] = []

  const flushList = () => {
    if (listItems.length) {
      output.push(`<ul class="md-list">${listItems.map(i => `<li>${i}</li>`).join('')}</ul>`)
      listItems = []
    }
    if (orderedListItems.length) {
      output.push(`<ol class="md-ordered-list">${orderedListItems.map(i => `<li>${i}</li>`).join('')}</ol>`)
      orderedListItems = []
      inOrderedList = false
    }
  }

  for (let i = 0; i < outputLines.length; i++) {
    const line = outputLines[i]

    // Code block start/end
    if (line.startsWith('```')) {
      if (inCode) {
        const code = escapeHtml(codeLines.join('\n'))
        output.push(`<div class="code-block-wrap"><div class="code-block-header"><span>${codeLang || 'code'}</span><button class="copy-code-btn" onclick="navigator.clipboard.writeText(this.closest('.code-block-wrap').querySelector('code').innerText);this.textContent='已复制';setTimeout(()=>this.textContent='复制',2000)">复制</button></div><pre><code>${code}</code></pre></div>`)
        codeLines.length = 0
        codeLang = ''
        inCode = false
      } else {
        flushList()
        codeLang = line.slice(3).trim()
        inCode = true
      }
      continue
    }
    if (inCode) {
      codeLines.push(line)
      continue
    }

    // 处理包含 [文本](链接) 格式的行
    if (/\[([^\]]+)\]\(([^)]+)\)/.test(line)) {
      const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
      let match;
      let lastIndex = 0;
      let processedLine = '';

      while ((match = regex.exec(line)) !== null) {
        const [fullMatch, linkText, linkUrl] = match;

        // 添加匹配前的文本
        processedLine += escapeHtml(line.slice(lastIndex, match.index));

        // 添加处理后的链接 - 只显示中括号中的文本，点击时下载
        processedLine += `<a href="#" onclick="event.preventDefault(); window.portDownload('${linkUrl}'); return false;">${escapeHtml(linkText)}</a>`;

        lastIndex = match.index + fullMatch.length;
      }

      // 添加剩余的文本
      processedLine += escapeHtml(line.slice(lastIndex));

      // 替换原始行
      output.push(`<p>${processedLine}</p>`);
      continue;
    }

    // Unordered list items (- or *)
    if (/^[-*+] /.test(line)) {
      flushList()
      listItems.push(processInline(line.slice(2)))
      continue
    }

    // Ordered list items (1. 2. 3.)
    if (/^\d+\.\s/.test(line)) {
      if (!inOrderedList) {
        flushList()
        inOrderedList = true
      }
      orderedListItems.push(processInline(line.replace(/^\d+\.\s/, '')))
      continue
    }

    flushList()

    // Table (simple)
    if (line.startsWith('|') && line.endsWith('|')) {
      if (line.includes('---')) continue // separator
      const cells = line.slice(1, -1).split('|').map(c => c.trim())
      output.push(`<tr>${cells.map(c => `<td>${processInline(c)}</td>`).join('')}</tr>`)
      continue
    }

    // Headers
    if (line.startsWith('### ')) {
      output.push(`<h3>${processInline(line.slice(4))}</h3>`)
    } else if (line.startsWith('## ')) {
      output.push(`<h2>${processInline(line.slice(3))}</h2>`)
    } else if (line.startsWith('# ')) {
      output.push(`<h1>${processInline(line.slice(2))}</h1>`)
    } else if (line.startsWith('> ')) {
      output.push(`<blockquote>${processInline(line.slice(2))}</blockquote>`)
    } else if (line.startsWith('---')) {
      output.push('<hr>')
    } else if (line.trim() === '') {
      output.push('<div class="md-gap"></div>')
    } else {
      output.push(`<p>${processInline(line)}</p>`)
    }
  }
  flushList()

  let result = output.join('')
  result = result.replace(/(<tr>.*?<\/tr>)+/gs, (match) => `<table>${match}</table>`)

  return result
}

